import { useSession, useUser } from "@clerk/clerk-react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Textarea,
  Select,
  SelectItem,
} from "@heroui/react";
import {
  ChatText,
  Envelope,
  EnvelopeSimple,
  Phone,
  Plus,
  WhatsappLogo,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { CustomerInvoice } from "shared/types/customer-invoice";
import {
  IncassoAction,
  IncassoActionType,
  IncassoActionInputBy,
  INCASSO_ACTION_TYPE_LABELS,
} from "shared/types/incasso-action";

interface IncassoActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: CustomerInvoice | null;
  onActionCreated?: (action: IncassoAction) => void;
}

const ACTION_TYPE_OPTIONS = [
  { key: IncassoActionType.PHONE, label: "Telefoongesprek", icon: Phone },
  { key: IncassoActionType.WHATSAPP_MESSAGE, label: "WhatsApp bericht", icon: WhatsappLogo },
  { key: IncassoActionType.SMS, label: "SMS", icon: ChatText },
  { key: IncassoActionType.EMAIL, label: "E-mail", icon: Envelope },
  { key: IncassoActionType.REGISTERED_MAIL, label: "Aangetekende brief", icon: EnvelopeSimple },
];

export const IncassoActionModal = ({
  isOpen,
  onClose,
  invoice,
  onActionCreated,
}: IncassoActionModalProps) => {
  const { session } = useSession();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [actions, setActions] = useState<IncassoAction[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state
  const [selectedType, setSelectedType] = useState<IncassoActionType>(IncassoActionType.PHONE);
  const [remarks, setRemarks] = useState("");

  const partnerId = invoice?.partner_id?.[0];

  // Fetch existing actions when modal opens
  useEffect(() => {
    if (isOpen && partnerId) {
      fetchActions(partnerId);
    } else if (!isOpen) {
      // Reset state when modal closes
      setActions([]);
      setShowAddForm(false);
      setRemarks("");
      setSelectedType(IncassoActionType.PHONE);
    }
  }, [isOpen, partnerId]);

  const fetchActions = async (partnerId: number) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-actions/partner/${partnerId}`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setActions(data);
    } catch (error) {
      console.error("Error fetching incasso actions:", error);
      toast.error("Fout bij het ophalen van incasso acties");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAction = useCallback(async () => {
    if (!partnerId || !user?.id) return;

    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-actions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            actionType: selectedType,
            remarks: remarks || undefined,
            odooPartnerId: partnerId,
            contactId: invoice?.contact?.id,
            companyId: invoice?.company?.id,
            inputBy: IncassoActionInputBy.USER,
          }),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to create action");
      }

      const newAction = await response.json();
      setActions((prev) => [newAction, ...prev]);
      setShowAddForm(false);
      setRemarks("");
      setSelectedType(IncassoActionType.PHONE);
      toast.success("Incasso actie toegevoegd");
      onActionCreated?.(newAction);
    } catch (error) {
      console.error("Error creating incasso action:", error);
      toast.error("Fout bij het aanmaken van incasso actie");
    } finally {
      setIsSaving(false);
    }
  }, [partnerId, selectedType, remarks, invoice, session, user?.id, onActionCreated]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (type: IncassoActionType) => {
    const option = ACTION_TYPE_OPTIONS.find((o) => o.key === type);
    if (option) {
      const Icon = option.icon;
      return <Icon weight="duotone" size={16} />;
    }
    return <Phone weight="duotone" size={16} />;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Phone weight="duotone" size={24} className="text-moss-primary" />
            <span>Incasso acties - {invoice?.invoice_partner_display_name}</span>
          </div>
        </ModalHeader>

        <ModalBody>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner label="Acties ophalen..." color="success" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Add new action form */}
              {showAddForm ? (
                <div className="border rounded-lg p-4 bg-neutral-50">
                  <h4 className="text-sm font-medium mb-3">Nieuwe actie toevoegen</h4>
                  <div className="flex flex-col gap-3">
                    <Select
                      label="Type actie"
                      selectedKeys={[selectedType]}
                      onSelectionChange={(keys) => {
                        const key = Array.from(keys)[0] as IncassoActionType;
                        if (key) setSelectedType(key);
                      }}
                      size="sm"
                    >
                      {ACTION_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.key} startContent={<option.icon size={16} />}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </Select>

                    <Textarea
                      label="Opmerkingen"
                      placeholder="Voeg eventuele opmerkingen toe..."
                      value={remarks}
                      onValueChange={setRemarks}
                      size="sm"
                      minRows={2}
                    />

                    <div className="flex justify-end gap-2">
                      <ButtonNew
                        variant="secondaryNeutral"
                        label="Annuleren"
                        onClick={() => {
                          setShowAddForm(false);
                          setRemarks("");
                        }}
                      />
                      <ButtonNew
                        variant="primaryMoss"
                        label={isSaving ? "Opslaan..." : "Opslaan"}
                        onClick={handleCreateAction}
                        isDisabled={isSaving}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <ButtonNew
                  variant="secondaryMoss"
                  icon={<Plus size={14} />}
                  label="Nieuwe actie"
                  onClick={() => setShowAddForm(true)}
                />
              )}

              {/* Actions list */}
              <div className="flex flex-col gap-2">
                <h4 className="text-sm font-medium text-neutral-secondary-mid">
                  Geschiedenis ({actions.length} acties)
                </h4>

                {actions.length === 0 ? (
                  <p className="text-sm text-neutral-secondary-mid italic py-4 text-center">
                    Nog geen incasso acties geregistreerd
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-hidden divide-y">
                    {actions.map((action) => (
                      <div
                        key={action.id}
                        className="flex items-start gap-3 p-3 hover:bg-neutral-50"
                      >
                        <div className="p-2 rounded-full bg-moss-primary/10 text-moss-primary">
                          {getActionIcon(action.actionType)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {INCASSO_ACTION_TYPE_LABELS[action.actionType]}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-secondary-mid">
                              {action.inputBy === IncassoActionInputBy.SYSTEM ? "Systeem" : "Gebruiker"}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-secondary-mid mt-0.5">
                            {formatDate(action.actionDate)}
                          </p>
                          {action.remarks && (
                            <p className="text-sm text-neutral-secondary-mid mt-1">
                              {action.remarks}
                            </p>
                          )}
                          {action.phoneNumber && (
                            <p className="text-xs text-neutral-secondary-mid mt-1">
                              Tel: {action.phoneNumber}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonNew variant="secondaryNeutral" label="Sluiten" onClick={onClose} />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default IncassoActionModal;
